import { background, reference } from '@carvana/ds-styles';
import { useContext, useMemo } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useIsNative } from '~/context/common/DeviceContext';
import { useHasValidSoftPull } from '~/context/home/FinanceContext';
import ModalContext from '~/context/home/ModalContext';
import { nativeStcPayload } from '~/native/common/utils';
import { useNativeOpenSoftPullOnClick } from '~/native/home/hooks';
import optimizeImage, {
  type Options as OptimizeOptions
} from '~/utilities/optimizeImage';
import type { SlideProps } from './Slide';

const commonOptions: OptimizeOptions = {
  format: 'auto',
  quality: 90
};

type SlideContent = Omit<
  SlideProps,
  'opacity' | 'spacerHeading' | 'spacerSubHeading'
>;

export default function useSlideContents(): SlideContent[] {
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const isNative = useIsNative();
  const handleNativeOpenSoftPull = useNativeOpenSoftPullOnClick();
  const hasValidSoftPull = useHasValidSoftPull();

  return useMemo<SlideContent[]>(
    () => [
      {
        backgroundColor: reference.slate['700'],
        cta: {
          action: '/value-tracker',
          text: 'Get Your Value'
        },
        heading: 'Stay up to speed with your car’s value',
        image: {
          alt: 'A man on a couch looking at his phone',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/value-tracker-desktop.jpg`,
            commonOptions
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/value-tracker-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/value-tracker-tablet.jpg`,
            commonOptions
          )
        },
        subHeading:
          'Get personalized insights on your car’s value and track it over time with Carvana Value Tracker.'
      },
      ...(hasValidSoftPull
        ? []
        : new Array<SlideContent>({
            backgroundColor: reference.slate['700'],
            cta: {
              action: ev => {
                handleNativeOpenSoftPull(ev);
                if (!isNative) setSoftPullModalOpen(true);
              },
              text: 'Get Your Terms'
            },
            heading: 'Save time with Carvana, just like Shaq did',
            image: {
              alt: 'Shaquille O’Neal holding a phone after buying a car on Carvana',
              desktop: optimizeImage(
                `${appAssetsCdnUrl}/images/home/marketing-carousel/save-time-like-shaq-desktop.jpg`,
                commonOptions
              ),
              mobile: optimizeImage(
                `${appAssetsCdnUrl}/images/home/marketing-carousel/save-time-like-shaq-mobile.jpg`,
                commonOptions
              ),
              tablet: optimizeImage(
                `${appAssetsCdnUrl}/images/home/marketing-carousel/save-time-like-shaq-tablet.jpg`,
                commonOptions
              )
            },
            subHeading:
              'Get pre-qualified instantly to shop 100% online with personalized terms. No hit to your credit.'
          })),
      {
        backgroundColor: background.medium,
        cta: {
          action: '/insurance',
          text: 'Learn More'
        },
        heading: 'Insurance coverage in 3 clicks™',
        image: {
          alt: 'Carvana insurance built with Root',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/carvana-insurance-desktop.jpg`,
            commonOptions
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/carvana-insurance-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/carvana-insurance-tablet.jpg`,
            commonOptions
          )
        },
        subHeading:
          'Carvana Insurance powered by Root is designed for you. Get quick, custom coverage in 3 clicks™ while we handle the rest.'
      },
      {
        backgroundColor: reference.slate['700'],
        cta: {
          action: '/sell-my-car',
          nativePayload: nativeStcPayload,
          text: 'Get Your Offer'
        },
        heading: 'Sell your car to Carvana',
        image: {
          alt: 'A Carvana employee at a customer’s house, delivering a check, with the customer’s car on a Carvana hauler in the background',
          desktop: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/sell-to-carvana-desktop.jpg`,
            commonOptions
          ),
          mobile: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/sell-to-carvana-mobile.jpg`,
            commonOptions
          ),
          tablet: optimizeImage(
            `${appAssetsCdnUrl}/images/home/marketing-carousel/sell-to-carvana-tablet.jpg`,
            commonOptions
          )
        },
        mobileImageHeight: 'h-[357px]',
        subHeading: 'Get a real offer in under 2 minutes.'
      }
    ],
    [handleNativeOpenSoftPull, hasValidSoftPull, isNative, setSoftPullModalOpen]
  );
}
